import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { FormFieldController } from "@src/appV2/PlacementCandidates/components/FormFieldController";
// eslint-disable-next-line no-restricted-imports
import statesList from "@src/constants/states";
import { type Control, useWatch } from "react-hook-form";

import { useGetCounties } from "../api/useGetCounties";
import { useGetMSAs } from "../api/useGetMSAs";
import { MappingLevel, type RequirementMappingFormValues } from "../types";
import { HCFRequiredBySelectField } from "./HCFRequiredBySelectField";

interface RequiredBySelectFieldProps {
  hcfId?: string;
  control: Control<RequirementMappingFormValues>;
}

interface FieldConfig {
  label: string;
  options: string[];
  isLoading: boolean;
}

export function RequiredBySelectField(props: RequiredBySelectFieldProps) {
  const { control, hcfId } = props;
  const { data: countyOptions, isLoading: isLoadingCounties } = useGetCounties();
  const { data: msaOptions, isLoading: isLoadingMSAs } = useGetMSAs();
  const level = useWatch({
    control,
    name: "level",
  });

  if (level === MappingLevel.GLOBAL) {
    return null;
  }

  if (level === MappingLevel.HCF) {
    return <HCFRequiredBySelectField control={control} hcfId={hcfId} />;
  }

  // For level === MappingLevel.STATE
  let fieldConfig: FieldConfig = {
    label: "State",
    options: statesList,
    isLoading: false,
  };

  if (level === MappingLevel.COUNTY) {
    fieldConfig = {
      label: "County",
      options: countyOptions ?? [],
      isLoading: isLoadingCounties,
    };
  } else if (level === MappingLevel.MSA) {
    fieldConfig = {
      label: "MSA",
      options: msaOptions ?? [],
      isLoading: isLoadingMSAs,
    };
  }

  return (
    <FormFieldController
      required
      name="requiredBy"
      control={control}
      label={fieldConfig.label}
      sx={{ mb: 2 }}
      render={(field) => (
        <Autocomplete
          multiple
          options={fieldConfig.options}
          loading={fieldConfig.isLoading}
          value={field.value}
          renderInput={(params) => (
            <TextField
              {...params}
              // @ts-expect-error - field not typed correctly
              // FIXME: Automatically disabled during ESLint config update
              error={Boolean(field.error)}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {fieldConfig.isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          onChange={(_, newValue) => {
            field.onChange(newValue);
          }}
          onBlur={field.onBlur}
        />
      )}
    />
  );
}
